import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAccountReport,
  useGetBalanceSheetReport,
  useGetForecastReport,
  useGetPaymentReport,
  useGetPLReport,
  useGetPurchaseReport,
  useGetSupplierReport,
  useGetTrialBalanceReport,
} from "src/api/reports";
import DateRangeInput from "src/shared/components/date-range";
import ReportsIcon from "src/assets/svg-icons/side-bar/reports.svg";
import { useTranslation } from "react-i18next";
import youtubeIcon from "src/assets/svg-icons/aggregators/youtube.svg";
import { downloadAsXLS, specificBranchHasPermission } from "src/utils";
import ReportBox from "./report-box";
import { useGetSuppliersList } from "src/api/purchasing/suppliers";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useGetOCAccountsList } from "src/api/operation-cost/accounts";
import { useGetBranches } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
const ReportsPages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("reports");
  const { t: T } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: moment().startOf("month").format("yyyy-MM-DD"),
    endDate: moment().endOf("month").format("yyyy-MM-DD"),
  });

  const [supplierId, setSupplierId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [branches, setBranches] = useState<number[]>([]);

  // APIS
  const { data: suppliersData } = useGetSuppliersList();
  const { data: accountsData } = useGetOCAccountsList();
  const { data: branchesData } = useGetBranches();

  const {
    data: paymentReportData,
    refetch: refetchPaymentReport,
    isFetching: isFetchingPaymentReport,
    status: paymentReportStatus,
  } = useGetPaymentReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    ...(branches.length > 0 ? { branches: branches } : {}),
    enabled: false,
  });

  const {
    data: purchaseReportData,
    refetch: refetchPurchaseReport,
    isFetching: isFetchingPurchaseReport,
    status: purchaseReportStatus,
  } = useGetPurchaseReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    ...(branches.length > 0 ? { branches: branches } : {}),
  });

  const {
    data: forecastReportData,
    refetch: refetchForecastReport,
    isFetching: isFetchingForecastReport,
    status: forecastReportStatus,
  } = useGetForecastReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    ...(branches.length > 0 ? { branches: branches } : {}),
  });

  const {
    data: PLReportData,
    refetch: refetchPLReport,
    isFetching: isFetchingPLReport,
    status: PLReportStatus,
  } = useGetPLReport({
    //profit and loss report
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const {
    data: supplierReportData,
    refetch: refetchSupplierReport,
    isFetching: isFetchingSupplierReport,
    status: supplierReportStatus,
  } = useGetSupplierReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    supplier_id: supplierId,
    ...(branches.length > 0 ? { branches: branches } : {}),
  });

  const {
    data: accountReportData,
    refetch: refetchAccountReport,
    isFetching: isFetchingAccountReport,
    status: accountReportStatus,
  } = useGetAccountReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    account_id: accountId,
    ...(branches.length > 0 ? { branches: branches } : {}),
  });

  const {
    data: balanceSheetReportData,
    refetch: refetchBalanceSheetReport,
    isFetching: isFetchingBalanceSheetReport,
    status: balanceSheetReportStatus,
  } = useGetBalanceSheetReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const {
    data: trialBalanceData,
    refetch: refetchTrialBalanceReport,
    isFetching: isFetchingTrialBalance,
    status: trialBalanceStatus,
  } = useGetTrialBalanceReport({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });

  const handleChangeBranches = (
    event: SelectChangeEvent<number | string | (number | string)[]>
  ) => {
    const value = event.target.value;
    const arr = (
      typeof value === "string" ? value.split(",") : Array.isArray(value) ? value : [value]
    ) as (number | string)[];
    if (
      arr.some((x) => String(x) === REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)
    ) {
      setBranches([]);
      return;
    }
    const nums = arr
      .map((x) => (typeof x === "number" ? x : Number(String(x).trim())))
      .filter((n) => Number.isFinite(n) && n > 0);
    setBranches(nums);
  };

  const checkBranchesSelection = () => {
    if (branches.length === 0) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mustSelectBranches"),
        },
      ]);
      return false;
    }
    return true;
  };

  const handleDownloadPaymentReport = () => {
    refetchPaymentReport();
  };

  const handleDownloadPurchaseReport = () => {
    refetchPurchaseReport();
  };

  const handleDownloadForecastReport = () => {
    refetchForecastReport();
  };

  const handleDownloadPLReport = () => {
    refetchPLReport();
  };

  const handleViewPLReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    if (branches.length === 1) {
      params.set("branch_id", String(branches[0]));
    }
    navigate(`/reports/profit-and-loss?${params.toString()}`);
  };

  const handleDownloadSupplierReport = () => {
    if (!supplierId) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mustSelectSupplier"),
        },
      ]);
      return;
    }
    refetchSupplierReport();
  };

  const handleDownloadAccountReport = () => {
    if (!accountId) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mustSelectLiability"),
        },
      ]);
      return;
    }
    refetchAccountReport();
  };

  const handleViewItemDetailReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/item-detail?${params.toString()}`);
  };

  const handleViewAggregatorStatementReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/aggregator-statement?${params.toString()}`);
  };

  const handleDownloadBalanceSheetReport = () => {
    refetchBalanceSheetReport();
  };

  const handleViewPaymentMovementReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/payment-movement?${params.toString()}`);
  };

  const handleViewTopItemsFluctuations = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/top-items-fluctuations?${params.toString()}`);
  };

  const handleViewTaxReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/tax?${params.toString()}`);
  };

  const handleViewAggregatorsPlReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/aggregators-pl?${params.toString()}`);
  };

  const handleViewOperationExpensesReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/operation-expenses?${params.toString()}`);
  };

  const handleDownloadTrialBalance = () => {
    refetchTrialBalanceReport();
  };
  const handleViewAccountStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/account-statement?${params.toString()}`);
  };

  const handleViewSupplierStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/supplier-statement?${params.toString()}`);
  };

  const handleViewVendorStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/vendor-statement?${params.toString()}`);
  };

  const handleViewExpensesStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/expenses-statement?${params.toString()}`);
  };

  const handleViewTrialBalance = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/trial-balance?${params.toString()}`);
  };

  const handleViewBalanceSheet = () => {
    navigate(`/reports/balance-sheet`);
  };

  useEffect(() => {
    if (
      paymentReportStatus === "success" &&
      !!paymentReportData &&
      !isFetchingPaymentReport
    ) {
      const utf8CSVData = `\uFEFF${paymentReportData}`;
      downloadAsXLS(utf8CSVData, "payments-report");
    }
  }, [paymentReportStatus, paymentReportData, isFetchingPaymentReport]);

  useEffect(() => {
    if (
      purchaseReportStatus === "success" &&
      !!purchaseReportData &&
      !isFetchingPurchaseReport
    ) {
      const utf8CSVData = `\uFEFF${purchaseReportData}`;
      downloadAsXLS(utf8CSVData, "purchase-report");
    }
  }, [purchaseReportStatus, purchaseReportData, isFetchingPurchaseReport]);

  useEffect(() => {
    if (
      forecastReportStatus === "success" &&
      !!forecastReportData &&
      !isFetchingForecastReport
    ) {
      const utf8CSVData = `\uFEFF${forecastReportData}`;
      downloadAsXLS(utf8CSVData, "forecast-report");
    }
  }, [forecastReportStatus, forecastReportData, isFetchingForecastReport]);

  useEffect(() => {
    if (PLReportStatus === "success" && !!PLReportData && !isFetchingPLReport) {
      const utf8CSVData = `\uFEFF${PLReportData}`;
      downloadAsXLS(utf8CSVData, "profit-loss-report");
    }
  }, [PLReportStatus, PLReportData, isFetchingPLReport]);

  useEffect(() => {
    if (
      supplierReportStatus === "success" &&
      !!supplierReportData &&
      !isFetchingSupplierReport
    ) {
      const utf8CSVData = `\uFEFF${supplierReportData}`;
      downloadAsXLS(utf8CSVData, "supplier-report");
    }
  }, [supplierReportStatus, supplierReportData, isFetchingSupplierReport]);

  useEffect(() => {
    if (
      accountReportStatus === "success" &&
      !!accountReportData &&
      !isFetchingAccountReport
    ) {
      const utf8CSVData = `\uFEFF${accountReportData}`;
      downloadAsXLS(utf8CSVData, "liability-report");
    }
  }, [accountReportStatus, accountReportData, isFetchingAccountReport]);

  useEffect(() => {
    if (
      balanceSheetReportStatus === "success" &&
      !!balanceSheetReportData &&
      !isFetchingBalanceSheetReport
    ) {
      const utf8CSVData = `\uFEFF${balanceSheetReportData}`;
      downloadAsXLS(utf8CSVData, "balance-report");
    }
  }, [
    balanceSheetReportStatus,
    balanceSheetReportData,
    isFetchingBalanceSheetReport,
  ]);

  useEffect(() => {
    if (
      trialBalanceStatus === "success" &&
      !!trialBalanceData &&
      !isFetchingTrialBalance
    ) {
      const utf8CSVData = `\uFEFF${trialBalanceData}`;
      downloadAsXLS(utf8CSVData, "trial-balance");
    }
  }, [trialBalanceStatus, trialBalanceData, isFetchingTrialBalance]);

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id)
    ) || [];

  return (
    <Box gap={2} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        gap={2}
        flexWrap={"wrap"}
        mb={3}
      >
        <Stack
          direction={"row"}
          spacing={"12px"}
          alignItems={"center"}
          color={"#101828"}
        >
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"60px"}
            height={"60px"}
            bgcolor={"#D0D5DD"}
            borderRadius={"16px"}
          >
            <img src={ReportsIcon} width={"32px"} alt="" />
          </Box>
          <Box>
            <Typography
              fontSize={"32px"}
              lineHeight={"38.4px"}
              color={"#101828"}
            >
              {t("thirdEyeReport")}
            </Typography>
            <Typography fontSize={"20px"} lineHeight={"24px"} color={"#98A2B3"}>
              {t("listOfChosenReports")}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="outlined"
          color="error"
          sx={{ height: "48px", border: "1px solid  #B32318" }}
          startIcon={<img src={youtubeIcon} alt="" width={"34px"} />}
          href="https://www.youtube.com/watch?v=uxQmpaBLMpc"
          target="_blank"
        >
          {T("viewHelp")}
        </Button>
      </Box>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={2}
      >
        <Typography color={"#475467"} fontWeight={600}>
          {t("availableReports")}
        </Typography>
        <Stack direction={"row"} gap={2}>
          <FormControl fullWidth size="small" sx={{ minWidth: 183 }}>
            <InputLabel id="demo-multiple-checkbox-label">
              {T("branches")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              value={branches}
              onChange={handleChangeBranches}
              input={<OutlinedInput label={T("branches")} />}
              renderValue={(selected) => {
                if (!selected?.length)
                  return t("allBranches");
                return selected
                  .map(
                    (selectedId: number) =>
                      branchesData?.data.find(
                        (branch: any) => branch.id === +selectedId
                      )?.name
                  )
                  .filter(Boolean)
                  .join(", ");
              }}
              // MenuProps={MenuProps}
            >
              <MenuItem
                key="__all-branches-reports-index"
                value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
                sx={{
                  padding: "0",
                  borderBottom: "1px solid #EAECF0",
                  bgcolor: "#F9FAFB",
                }}
              >
                <Typography fontWeight={600} variant="body2" sx={{ px: 1, py: 1 }}>
                  {t("allBranches")}
                </Typography>
              </MenuItem>
              {branchesHavePermission.map((branch) => (
                <MenuItem
                  key={branch.id}
                  value={branch.id}
                  sx={{ padding: "0" }}
                >
                  <Checkbox
                    size="small"
                    checked={branches.indexOf(branch.id) > -1}
                  />
                  <ListItemText primary={branch.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 250 }}>
            <DateRangeInput
              startDate={
                dateRange.startDate ? moment(dateRange.startDate) : null
              }
              startDateId={`your_unique_start_date_id`}
              endDate={dateRange.endDate ? moment(dateRange.endDate) : null}
              endDateId={`your_unique_end_date_id`} // string;
              onDatesChange={(arg: {
                startDate: moment.Moment | null;
                endDate: moment.Moment | null;
              }) => {
                setDateRange({
                  startDate: arg.startDate?.format("yyyy-MM-DD") as string,
                  endDate: arg.endDate?.format("yyyy-MM-DD") as string,
                });
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("OCReport")}
            onView={handleViewOperationExpensesReport}
            isLoading={false}
          />
        </Grid>
        {/* <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("paymentReport")}
            onDownload={handleDownloadPaymentReport}
            isLoading={isFetchingPaymentReport}
          />
        </Grid> */}
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("purchaseReport")}
            onDownload={handleDownloadPurchaseReport}
            isLoading={isFetchingPurchaseReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("taxReport")}
            onView={handleViewTaxReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("forecastReport")}
            onDownload={handleDownloadForecastReport}
            isLoading={isFetchingForecastReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("aggsReport")}
            onView={handleViewAggregatorsPlReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("NewPLReport")}
            onView={handleViewPLReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("PLReport")}
            onDownload={handleDownloadPLReport}
            isLoading={isFetchingPLReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("supplierReport")}
            onDownload={handleDownloadSupplierReport}
            isLoading={isFetchingSupplierReport}
            options={suppliersData?.data}
            value={supplierId}
            setValue={setSupplierId}
            selectLabel={t("supplier")}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("liabilitiesReport")}
            onDownload={handleDownloadAccountReport}
            isLoading={isFetchingAccountReport}
            options={accountsData?.data}
            value={accountId}
            setValue={setAccountId}
            selectLabel={t("liability")}
            searchableSelect
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("aggsStatementReport")}
            onView={handleViewAggregatorStatementReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("balanceSheet")}
            onDownload={handleDownloadBalanceSheetReport}
            isLoading={isFetchingBalanceSheetReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("paymentTransactions")}
            onView={handleViewPaymentMovementReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("itemDetailReport")}
            onView={handleViewItemDetailReport}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={purchasingT("topItemsFluctuations")}
            onView={handleViewTopItemsFluctuations}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("trialBalance")}
            onDownload={handleDownloadTrialBalance}
            isLoading={isFetchingTrialBalance}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("accountStatement")}
            onView={handleViewAccountStatement}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("supplierStatementReport")}
            onView={handleViewSupplierStatement}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("vendorStatementReport")}
            onView={handleViewVendorStatement}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={t("expensesStatementReport")}
            onView={handleViewExpensesStatement}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={`${T("new")} ${t("trialBalance")}`}
            onView={handleViewTrialBalance}
            isLoading={false}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportBox
            name={`${T("new")} ${t("balanceSheet")}`}
            onView={handleViewBalanceSheet}
            isLoading={false}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPages;